// @ts-strict

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { environment } from '@env/environment';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { CAMPAIGNS_CAMPAIGNS_SIMILAR_GET, CAMPAIGNS_CAMPAIGNS_SPECIAL_GET } from '../../helpers/api.constants';
import {
  BaseApiResponse,
  FilterType,
  IActionButtonData,
  ISingleCampaign,
  ISortMethodData,
  PageableApiResponse,
  SpecialCampaignData,
  SpecialCampaignsResponse
} from '../../models/core-models';
import { AvailableCampaignActionButtonsOptions } from './campaigns-list.service.types';

@Injectable({
  providedIn: 'root'
})
export class CampaignsListService implements OnDestroy {
  public currentPage = 1;
  public _urlSubject = new Subject<string>();
  public $url = this._urlSubject.asObservable();

  public expandedCampaignId = new BehaviorSubject<number | null>(null);
  private CAMPAIGNS_URL: string | undefined;
  private destroy$ = new Subject<boolean>();

  constructor(private http: HttpClient) {
    this.$url
      .pipe(
        tap(url => (this.CAMPAIGNS_URL = url)),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public getCampaignActionButtonsOptions = ({
    status,
    has_deep_link,
    has_banners,
    has_hidelink,
    has_products,
    has_configurations,
    exclusive,
    exclusive_user_has_access
  }: Required<
    Pick<ISingleCampaign, 'has_banners' | 'has_deep_link' | 'has_hidelink' | 'has_products' | 'status' | 'exclusive' | 'exclusive_user_has_access'>
  > & {
    has_configurations: boolean;
  }): IActionButtonData[] => {
    const isCampaignActive = status === 'active';
    const isNotUnavailableExclusive = !(exclusive && !exclusive_user_has_access);
    // const isNotUnavailableExclusive = false;
    const availableOptions: Array<Omit<IActionButtonData, 'text'> & { visible: boolean; text: AvailableCampaignActionButtonsOptions }> = [
      {
        icon: 'eye',
        class: '',
        external: true,
        text: 'see_campaign_page',
        visible: true
      },
      {
        icon: 'user-plus',
        class: exclusive.toString() + exclusive_user_has_access.toString(),
        external: false,
        text: 'join_campaign',
        visible: !isCampaignActive && isNotUnavailableExclusive
      },
      {
        icon: 'copy',
        class: '',
        external: false,
        text: 'copy_link',
        visible: true
      },
      {
        icon: 'plus',
        class: '',
        external: false,
        text: 'url_address',
        visible: isCampaignActive
      },
      {
        icon: 'plus',
        class: '',
        external: false,
        text: 'deep_link',
        visible: has_deep_link && isCampaignActive
      },
      {
        icon: 'plus',
        class: '',
        external: false,
        text: 'banner',
        visible: has_banners && isCampaignActive
      },
      {
        icon: 'plus',
        class: '',
        external: false,
        text: 'postback_url',
        visible: has_configurations && isCampaignActive
      },
      {
        icon: 'plus',
        class: '',
        external: false,
        text: has_hidelink ? 'hidelink' : 'add_hide_link',
        visible: has_hidelink && isCampaignActive
      },
      {
        icon: 'plus',
        class: '',
        external: false,
        text: 'products',
        visible: has_products && isCampaignActive
      }
    ];

    return availableOptions.filter(({ visible }) => visible).map(({ visible, ...rest }) => ({ ...rest }));
  };

  public getCampaignsList(
    pageNumber: number,
    filters: FilterType,
    sortValues: ISortMethodData
  ): Observable<PageableApiResponse<{ campaigns?: ISingleCampaign[] }>> {
    this.currentPage = pageNumber;
    const params = { ...filters, order_field: sortValues.order_field, order_type: sortValues.order_type };

    return this.http.get<PageableApiResponse<{ campaigns?: ISingleCampaign[] }>>(environment.apiUrl + this.CAMPAIGNS_URL + `?page=${pageNumber}`, { params });
  }

  public returnCurrentPage(): number {
    return this.currentPage;
  }

  public getSimilarCampaigns(programID: number): Observable<BaseApiResponse<{ related_campaigns?: SpecialCampaignData[] }>> {
    return this.http.get<BaseApiResponse<{ related_campaigns?: SpecialCampaignData[] }>>(environment.apiUrl + CAMPAIGNS_CAMPAIGNS_SIMILAR_GET(programID));
  }

  public getSpecialCampaigns(
    type: string,
    reqParams?: {
      page: number;
      per_page: number;
      order_field: string;
      order_type: string;
      search?: string | undefined;
    }
  ): Observable<PageableApiResponse<SpecialCampaignsResponse>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    let params = {};
    if (reqParams) {
      params = { ...reqParams, type };
    } else {
      params = {
        type
      };
    }

    return this.http.get<PageableApiResponse<SpecialCampaignsResponse>>(environment.apiUrl + CAMPAIGNS_CAMPAIGNS_SPECIAL_GET, { headers, params });
  }
}
